import React, { useState, useEffect, useCallback } from 'react';
import { message, Space, Tag, Button, Popconfirm, Tabs } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { TableProps } from 'antd';
import GenericTable from '../../../components/common/Table';
import GenericSearch from '../../../components/common/Search';
import { SearchFilters } from '../../../types/TableTypes';
import { IPropertySale, ISpecialAlertCondition } from 'types/PropertyTypes';
import * as alertConditionApi from '../../../services/alertConditionApi';
import * as specialAlertConditionApi from '../../../services/specialAlertConditionApi';

const formatJapaneseDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";

  let date: Date;

  if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6)) - 1;
    const day = parseInt(dateString.substring(6, 8));
    date = new Date(year, month, day);
  } else {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return "N/A";

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}年${month}月${day}日`;
};


const getNotificationPropertyColumns = (onDelete: (id: number) => void): TableProps<IPropertySale>["columns"] => [
  {
    title: "登録日",
    dataIndex: "registered_at",
    key: "registered_at",
    width: 120,
    render: (text) => formatJapaneseDate(text),
    sorter: (a, b) => {
      const dateA = a.registered_at ? new Date(a.registered_at).getTime() : 0;
      const dateB = b.registered_at ? new Date(b.registered_at).getTime() : 0;
      return dateA - dateB;
    },
  },
  {
    title: "登録タイトル",
    dataIndex: "title",
    key: "title",
    width: 200,
    sorter: (a, b) =>
      (a.title || "").localeCompare(b.title || ""),
  },
  {
    title: "都道府県 区",
    dataIndex: "prefecture_district",
    key: "prefecture_district",
    width: 150,
    render: (_, record) => {
      const prefecture = record.prefecture || "";
      const district = record.district || "";
      const combined = [prefecture, district].filter(x => x).join(" ");
      return combined || "N/A";
    },
    sorter: (a, b) => {
      const prefA = `${a.prefecture || ""} ${a.district || ""}`;
      const prefB = `${b.prefecture || ""} ${b.district || ""}`;
      return prefA.localeCompare(prefB);
    },
  },
  {
    title: "平米数",
    dataIndex: "area_m2_range",
    key: "area_m2_range",
    width: 150,
    render: (_, record) => {
      const min = record.area_m2_min;
      const max = record.area_m2_max;
      if (min && max) {
        return `${min}～${max}㎡`;
      } else if (min) {
        return `${min}㎡以上`;
      } else if (max) {
        return `${max}㎡以下`;
      }
      return "-";
    },
    sorter: (a, b) => {
      const getMinValue = (record: any): number => record.area_m2_min || 0;
      return getMinValue(a) - getMinValue(b);
    },
  },
  {
    title: "駅徒歩",
    dataIndex: "walk_minutes_range",
    key: "walk_minutes_range",
    width: 150,
    render: (_, record) => {
      const min = record.walk_minutes_min;
      const max = record.walk_minutes_max;
      if (min && max) {
        return `${min}～${max}分`;
      } else if (min) {
        return `${min}分以上`;
      } else if (max) {
        return `${max}分以下`;
      }
      return "-";
    },
    sorter: (a, b) => {
      const getMinValue = (record: any): number => record.walk_minutes_min || 0;
      return getMinValue(a) - getMinValue(b);
    },
  },
  {
    title: "坪単価",
    dataIndex: "price_per_tsubo_range",
    key: "price_per_tsubo_range",
    width: 150,
    render: (_, record) => {
      const min = record.price_per_tsubo_min;
      const max = record.price_per_tsubo_max;
      if (min && max) {
        return `${min.toLocaleString()}～${max.toLocaleString()}万円`;
      } else if (min) {
        return `${min.toLocaleString()}万円以上`;
      } else if (max) {
        return `${max.toLocaleString()}万円以下`;
      }
      return "-";
    },
    sorter: (a, b) => {
      const getMinValue = (record: any): number => record.price_per_tsubo_min || 0;
      return getMinValue(a) - getMinValue(b);
    },
  },
  {
    title: "平米単価",
    dataIndex: "price_per_m2",
    key: "price_per_m2",
    width: 150,
    render: (_, record) => {
      if (record.price_per_m2) {
        return `${record.price_per_m2.toLocaleString()}万円/㎡`;
      }
      return "-";
    },
    sorter: (a, b) => {
      const priceA = a.price_per_m2 || 0;
      const priceB = b.price_per_m2 || 0;
      return priceA - priceB;
    },
  },
  {
    title: "築年数",
    dataIndex: "built_year_range",
    key: "built_year_range",
    width: 150,
    render: (_, record) => {
      const min = record.built_year_min;
      const max = record.built_year_max;
      if (min && max) {
        return `${min}～${max}年`;
      } else if (min) {
        return `${min}年以上`;
      } else if (max) {
        return `${max}年以下`;
      }
      return "-";
    },
    sorter: (a, b) => {
      const getMinValue = (record: any): number => record.built_year_min || 0;
      return getMinValue(a) - getMinValue(b);
    },
  },
  {
    title: "ステータス",
    dataIndex: "is_active",
    key: "is_active",
    width: 100,
    render: (isActive) => (
      <Tag color={isActive ? 'green' : 'red'}>
        {isActive ? 'アクティブ' : '無効'}
      </Tag>
    ),
    sorter: (a, b) => {
      const statusA = a.is_active ? 1 : 0;
      const statusB = b.is_active ? 1 : 0;
      return statusA - statusB;
    },
  },
  {
    title: "操作",
    key: "action",
    width: 100,
    fixed: 'right',
    render: (_, record) => (
      <Space size='small'>
        <a
          href={`/notification-properties/edit/${record.id || ""}`}
          className='action-link'
        >
          編集
        </a>
        <Popconfirm
          title="この通知条件を削除しますか？"
          description="削除すると元に戻せません。"
          onConfirm={() => record.id && onDelete(record.id)}
          okText="削除"
          cancelText="キャンセル"
          okButtonProps={{ danger: true }}
        >
          <a className='action-link' style={{ color: '#ff4d4f' }}>
            削除
          </a>
        </Popconfirm>
      </Space>
    ),
  },
];

const NotificationPropertyList: React.FC = () => {
  const [tableData, setTableData] = useState<IPropertySale[]>([]);
  const [allData, setAllData] = useState<IPropertySale[]>([]);
  const [filteredData, setFilteredData] = useState<IPropertySale[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState<number>(0);
  const [sortInfo, setSortInfo] = useState<{ field?: string; order?: 'ascend' | 'descend' }>({});

  const handleDelete = useCallback(async (id: number) => {
    try {
      await alertConditionApi.del(id);

      message.success('通知条件を削除しました');

      const newAllData = allData.filter(item => item.id !== id);
      const newFilteredData = filteredData.filter(item => item.id !== id);
      setAllData(newAllData);
      setFilteredData(newFilteredData);
      setTotal(newFilteredData.length);

      const sortedData = applySorting(newFilteredData);
      const maxPage = Math.max(1, Math.ceil(newFilteredData.length / pageSize));
      const newPage = currentPage > maxPage ? maxPage : currentPage;
      setCurrentPage(newPage);
      setTableData(loadPageData(newPage, pageSize, sortedData));
    } catch (error) {
      console.error('Failed to delete:', error);
      message.error('削除に失敗しました');
    }
  }, [allData, filteredData, currentPage, pageSize, sortInfo]);

  const filterData = (data: IPropertySale[], filters: SearchFilters): IPropertySale[] => {
    return data.filter(item => {
      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase();
        const searchText = `${item.title || ''} ${item.prefecture || ''} ${item.district || ''}`.toLowerCase();
        if (!searchText.includes(keyword)) {
          return false;
        }
      }

      return true;
    });
  };

  const loadPageData = (page: number, size: number, data: IPropertySale[]) => {
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  const applySorting = (data: IPropertySale[]): IPropertySale[] => {
    if (!sortInfo.field || !sortInfo.order) return data;

    const columns = getNotificationPropertyColumns(handleDelete);
    const column = columns?.find(col => col?.key === sortInfo.field);
    const sorter = column?.sorter;

    if (!sorter || typeof sorter !== 'function') return data;

    const sortedData = [...data].sort(sorter);
    return sortInfo.order === 'descend' ? sortedData.reverse() : sortedData;
  };

  const applySortingWithInfo = (data: IPropertySale[], sortInfo: { field?: string; order?: 'ascend' | 'descend' }): IPropertySale[] => {
    if (!sortInfo.field || !sortInfo.order) return data;

    const columns = getNotificationPropertyColumns(handleDelete);
    const column = columns?.find(col => col?.key === sortInfo.field);
    const sorter = column?.sorter;

    if (!sorter || typeof sorter !== 'function') return data;

    const sortedData = [...data].sort(sorter);
    return sortInfo.order === 'descend' ? sortedData.reverse() : sortedData;
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const { field, order } = sorter;
    const newSortInfo = { field, order };
    setSortInfo(newSortInfo);

    const sortedData = applySortingWithInfo(filteredData, newSortInfo);
    setTableData(loadPageData(currentPage, pageSize, sortedData));
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);

      const results = await alertConditionApi.getList();

      const data = results.map((element: any) => ({ ...element, key: element.id }));
      
      setAllData(data);
      setFilteredData(data);
      setCurrentPage(1);
      setTotal(data.length);

      const sortedData = applySorting(data);
      setTableData(loadPageData(1, pageSize, sortedData));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      message.error("データの取得に失敗しました");
      
      // エラー時も空配列でアプリが動作するように
      setAllData([]);
      setFilteredData([]);
      setTableData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (filters: SearchFilters) => {
    setSearchLoading(true);

    await new Promise(resolve => setTimeout(resolve, 300));

    const filtered = filterData(allData, filters);
    setFilteredData(filtered);
    setTotal(filtered.length);
    setCurrentPage(1);

    const sortedData = applySorting(filtered);
    setTableData(loadPageData(1, pageSize, sortedData));

    setSearchLoading(false);
    message.success(`${filtered.length}件の結果が見つかりました`);
  };

  const handleResetSearch = async () => {
    setSearchLoading(true);

    await new Promise(resolve => setTimeout(resolve, 200));

    setFilteredData(allData);
    setTotal(allData.length);
    setCurrentPage(1);

    const sortedData = applySorting(allData);
    setTableData(loadPageData(1, pageSize, sortedData));

    setSearchLoading(false);
    message.info('フィルターをリセットしました');
  };

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);

    const sortedData = applySorting(filteredData);
    setTableData(loadPageData(page, size, sortedData));
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const navigate = useNavigate();

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/notification-properties/new')}
          size="large"
        >
          新規登録
        </Button>
      </div>

      <GenericSearch
        onSearch={handleSearch}
        onReset={handleResetSearch}
        loading={searchLoading}
        showDateFilters={false}
        keywordPlaceholder="タイトル、都道府県、区で検索..."
      />

      <GenericTable<IPropertySale>
        data={tableData || []}
        columns={getNotificationPropertyColumns(handleDelete)}
        total={total}
        pageSize={pageSize}
        currentPage={currentPage}
        loading={loading || searchLoading}
        onPageChange={handlePageChange}
        onChange={handleTableChange}
        tableTitle=''
      />
    </>
  );
};

// === 特別条件一覧テーブルカラム定義 ===
const getSpecialConditionColumns = (onDelete: (id: number) => void): TableProps<ISpecialAlertCondition>["columns"] => [
  {
    title: "登録日",
    dataIndex: "registered_at",
    key: "registered_at",
    width: 120,
    render: (text) => formatJapaneseDate(text),
  },
  {
    title: "タイトル",
    dataIndex: "title",
    key: "title",
    width: 200,
  },
  {
    title: "エリア",
    dataIndex: "areas",
    key: "areas",
    width: 200,
    render: (areas: Array<{prefecture: string; district: string}>) => {
      if (!areas || areas.length === 0) return "-";
      return areas.map((a, i) => (
        <Tag key={i} style={{ marginBottom: 2 }}>{a.prefecture} {a.district}</Tag>
      ));
    },
  },
  {
    title: "間取り",
    dataIndex: "layout_types",
    key: "layout_types",
    width: 150,
    render: (types: string[]) => {
      if (!types || types.length === 0) return "-";
      return types.join(", ");
    },
  },
  {
    title: "駅徒歩",
    dataIndex: "walk_minutes_max",
    key: "walk_minutes_max",
    width: 80,
    render: (val) => val ? `${val}分以内` : "-",
  },
  {
    title: "築年数",
    dataIndex: "built_year_max",
    key: "built_year_max",
    width: 80,
    render: (val) => val ? `${val}年以内` : "-",
  },
  {
    title: "階数",
    dataIndex: "floor_min",
    key: "floor_min",
    width: 80,
    render: (val, record) => {
      if (!val) return "-";
      const pref = record.floor_preferred ? `(希望:${record.floor_preferred}階)` : "";
      return `${val}階以上${pref}`;
    },
  },
  {
    title: "ステータス",
    dataIndex: "is_active",
    key: "is_active",
    width: 100,
    render: (isActive) => (
      <Tag color={isActive ? 'green' : 'red'}>
        {isActive ? 'アクティブ' : '無効'}
      </Tag>
    ),
  },
  {
    title: "操作",
    key: "action",
    width: 100,
    fixed: 'right',
    render: (_, record) => (
      <Space size='small'>
        <a
          href={`/notification-properties/special/edit/${record.id || ""}`}
          className='action-link'
        >
          編集
        </a>
        <Popconfirm
          title="この特別条件を削除しますか？"
          description="削除すると元に戻せません。"
          onConfirm={() => record.id && onDelete(record.id)}
          okText="削除"
          cancelText="キャンセル"
          okButtonProps={{ danger: true }}
        >
          <a className='action-link' style={{ color: '#ff4d4f' }}>
            削除
          </a>
        </Popconfirm>
      </Space>
    ),
  },
];

// === 特別条件一覧コンポーネント ===
const SpecialConditionList: React.FC = () => {
  const [tableData, setTableData] = useState<ISpecialAlertCondition[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleDelete = useCallback(async (id: number) => {
    try {
      await specialAlertConditionApi.del(id);
      message.success('特別条件を削除しました');
      setTableData(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Failed to delete:', error);
      message.error('削除に失敗しました');
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const results = await specialAlertConditionApi.getList();
      const data = results.map((element: any) => ({ ...element, key: element.id }));
      setTableData(data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      message.error("データの取得に失敗しました");
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/notification-properties/special/new')}
          size="large"
        >
          特別条件を追加
        </Button>
      </div>

      <GenericTable<ISpecialAlertCondition>
        data={tableData || []}
        columns={getSpecialConditionColumns(handleDelete)}
        total={tableData.length}
        pageSize={10}
        currentPage={1}
        loading={loading}
        onPageChange={() => {}}
        tableTitle=''
      />
    </>
  );
};

// === メインコンポーネント（タブ切り替え） ===
const NotificationPropertyPage: React.FC = () => {
  return (
    <div className="dark-theme-container">
      <Tabs
        defaultActiveKey="normal"
        size="large"
        items={[
          {
            key: 'normal',
            label: '通常条件通知',
            children: <NotificationPropertyList />,
          },
          {
            key: 'special',
            label: '☆特別条件通知',
            children: <SpecialConditionList />,
          },
        ]}
      />
    </div>
  );
};

export default NotificationPropertyPage;
